import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { MagicWand, SealCheck, Spinner } from "@phosphor-icons/react";

const usePolledJob = (fetcher) => {
  const [info, setInfo] = useState(null);
  const pollRef = useRef(null);
  const load = () => fetcher().then(setInfo).catch(() => {});
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const watch = (onDone) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await fetcher();
        setInfo(s);
        if (s.job?.status !== "running") {
          clearInterval(pollRef.current);
          onDone(s.job);
        }
      } catch { /* keep polling */ }
    }, 3000);
  };
  return { info, load, watch };
};

export function DataQualityTools() {
  const enrich = usePolledJob(api.enrichLatest);
  const verify = usePolledJob(api.verifyLatest);

  const startEnrich = async () => {
    const n = enrich.info?.candidates || 0;
    if (!window.confirm(`Look up ${n.toLocaleString()} contact${n === 1 ? "" : "s"} on Hunter to fill missing job titles and LinkedIn profiles?\n\nExisting values are never overwritten. Each contact is only ever looked up once.`)) return;
    try {
      await api.enrichStart();
      enrich.load();
      enrich.watch((job) => {
        if (job?.status === "done") {
          const ct = job.counters || {};
          toast.success(`Enrichment complete — ${ct.filled_title || 0} job titles and ${ct.filled_linkedin || 0} LinkedIn profiles filled (${ct.not_found || 0} not found)`);
        } else {
          toast.error("Enrichment run did not finish — you can start it again");
        }
        enrich.load();
      });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const startVerify = async () => {
    const n = verify.info?.unverified || 0;
    if (!window.confirm(`Verify ${n.toLocaleString()} active contact email${n === 1 ? "" : "s"} via Hunter?\n\nThis uses 0.5 Hunter credits per address — each address is only ever checked once (results cached forever).`)) return;
    try {
      await api.verifyStart(null);
      verify.load();
      verify.watch((job) => {
        if (job?.status === "done") {
          const ct = job.counters || {};
          toast.success(`Verification complete — ${ct.deliverable || 0} deliverable, ${ct.risky || 0} risky, ${ct.undeliverable || 0} undeliverable`);
        } else {
          toast.error("Verification run did not finish — you can start it again");
        }
        verify.load();
      });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const eJob = enrich.info?.job, vJob = verify.info?.job;
  const eRunning = eJob?.status === "running", vRunning = vJob?.status === "running";
  const buckets = verify.info?.buckets || {};

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="data-quality-tools">
      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-2" data-testid="enrich-card">
        <div className="flex items-center gap-1.5 text-sm font-medium"><MagicWand size={15} className="text-violet-500" /> Fill missing titles & LinkedIn</div>
        <p className="text-xs text-zinc-500">
          Hunter looks up each contact by email and fills in their job title and LinkedIn profile — existing values are never touched.
        </p>
        {enrich.info && (
          <div className="text-xs text-zinc-600" data-testid="enrich-stats">
            <span className="font-medium text-zinc-900">{(enrich.info.missing_title || 0).toLocaleString()}</span> missing job title ·{" "}
            <span className="font-medium text-zinc-900">{(enrich.info.missing_linkedin || 0).toLocaleString()}</span> missing LinkedIn ·{" "}
            <span className="font-medium text-zinc-900">{(enrich.info.candidates || 0).toLocaleString()}</span> not looked up yet
          </div>
        )}
        {eRunning ? (
          <div className="text-xs text-zinc-600 flex items-center gap-1.5" data-testid="enrich-progress">
            <Spinner size={13} className="animate-spin" />
            Enriching… {eJob.progress?.done || 0} of {eJob.progress?.total || "?"} · {(eJob.counters?.filled_title || 0) + (eJob.counters?.filled_linkedin || 0)} fields filled
          </div>
        ) : (
          <button onClick={startEnrich} disabled={!(enrich.info?.candidates > 0)} data-testid="enrich-start-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
            <MagicWand size={13} /> {enrich.info?.candidates > 0 ? `Enrich ${enrich.info.candidates.toLocaleString()} contacts` : "Nothing to enrich"}
          </button>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-2" data-testid="verify-card">
        <div className="flex items-center gap-1.5 text-sm font-medium"><SealCheck size={15} className="text-emerald-600" /> Verify email deliverability</div>
        <p className="text-xs text-zinc-500">
          Hunter checks each address actually accepts mail, flagging risky and undeliverable ones before they hurt your bounce rate. 0.5 credits per address, checked once ever.
        </p>
        {verify.info && (
          <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="verify-stats">
            <span className="px-2 py-0.5 rounded-full border border-zinc-200 bg-zinc-50 text-zinc-600 font-medium">{(verify.info.unverified || 0).toLocaleString()} unverified</span>
            <span className="px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium">{(buckets.deliverable || 0).toLocaleString()} deliverable</span>
            <span className="px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 font-medium">{(buckets.risky || 0).toLocaleString()} risky</span>
            <span className="px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700 font-medium">{(buckets.undeliverable || 0).toLocaleString()} undeliverable</span>
          </div>
        )}
        {vRunning ? (
          <div className="text-xs text-zinc-600 flex items-center gap-1.5" data-testid="verify-bulk-progress">
            <Spinner size={13} className="animate-spin" />
            Verifying… {vJob.progress?.done || 0} of {vJob.progress?.total || "?"} checked
          </div>
        ) : (
          <button onClick={startVerify} disabled={!(verify.info?.unverified > 0)} data-testid="verify-start-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
            <SealCheck size={13} /> {verify.info?.unverified > 0 ? `Verify ${verify.info.unverified.toLocaleString()} emails` : "All verified"}
          </button>
        )}
      </div>
    </div>
  );
}
